import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState
} from 'react';
import { useTranslation } from 'react-i18next';

import hljs from 'highlight.js';
import 'highlight.js/styles/github-dark.css';

import { translateLanguage, multiLineHighlight } from '../../Helpers/Code.js';

import CodeLine from './Line.js';
import CommentsList from '../Comment/List.js';

function CodeTable(props) {
  const { t } = useTranslation();
  const [codeRows, setCodeRows] = useState([]);
  const [lineCommentBoxVisibility, setLineCommentBoxVisibility] = useState([]);
  const scrollRef = useRef(null);

  const toggleLineCommentBoxVisibility = useCallback((lineIndex) => {
    setLineCommentBoxVisibility(previousState => {
      const auxState = { ...previousState };
      auxState[lineIndex] = auxState[lineIndex] ? 0 : 1;
      return auxState;
    });
  }, []);

  const closeLineCommentBox = useCallback((lineIndex) => {
    setLineCommentBoxVisibility(previousState => {
      const auxState = { ...previousState };
      auxState[lineIndex] = 0;
      return auxState;
    });
  }, []);

  /**
   * The gutter columns are sized by the table rather than by their own
   * `width`, since the code cell asks for 100% and squeezes them down to
   * their content. Publish what they actually measure, plus the visible
   * width of the scroll container, so the plus column and the comment
   * threads can pin themselves to the right place while a long code line
   * scrolls underneath. A sticky offset guessed too wide would push those
   * cells on top of the code.
   */
  const syncStickyMetrics = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    if (scroller.clientWidth) {
      scroller.style.setProperty('--thread-w', `${scroller.clientWidth}px`);
    }
    const gutter = scroller.querySelector('td.line-number');
    const plus = scroller.querySelector('td.plus-square-line');
    if (gutter && gutter.offsetWidth) {
      scroller.style.setProperty('--gutter-w', `${gutter.offsetWidth}px`);
      if (plus && plus.offsetWidth) {
        scroller.style.setProperty(
          '--gutters-w', `${gutter.offsetWidth + plus.offsetWidth}px`
        );
      }
    }
  }, []);

  useLayoutEffect(() => {
    syncStickyMetrics();
  }, [codeRows, syncStickyMetrics]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncStickyMetrics);
      return () => window.removeEventListener('resize', syncStickyMetrics);
    }
    const observer = new ResizeObserver(syncStickyMetrics);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [syncStickyMetrics]);

  useEffect(() => {
    let codeLanguage = translateLanguage(props.code.language);
    let sourceCode = props.code.source_code;
    let highlightedCode;
    if (hljs.getLanguage(codeLanguage)) {
      highlightedCode =
        hljs.highlight(sourceCode, { language: codeLanguage }).value;
    } else {
      highlightedCode = hljs.highlightAuto(sourceCode).value;
    }
    let highlightedRows = multiLineHighlight(highlightedCode);
    let rows = [];
    highlightedRows.forEach((line, index) => {
      const ln = index + 1;
      const hasThread = !!(
        props.codeLinesComments &&
        props.codeLinesComments[ln] &&
        props.codeLinesComments[ln].length
      );
      const isBoxOpen = !!lineCommentBoxVisibility[ln];
      rows.push(
        <React.Fragment key={index}>
          <CodeLine
            code={line}
            index={index}
            hasThread={hasThread}
            toggleLineCommentBoxVisibility={toggleLineCommentBoxVisibility}
          />
          {(hasThread || isBoxOpen) && (
            <tr className="cs-thread-row">
              <td className="thread-gutter" />
              <td className="thread-gutter-plus" />
              <td className="thread-body" id={`codeline-${ln}-comments`}>
                <div className="cs-thread">
                  <CommentsList
                    addCommentVisibility={lineCommentBoxVisibility[ln]}
                    codeId={props.code.id}
                    comments={props.codeLinesComments[ln]}
                    isCodeLineComment={1}
                    onCloseAddComment={closeLineCommentBox}
                    updateComments={props.updateComments}
                    userData={props.userData}
                    lineNumber={ln}
                  />
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
    setCodeRows(rows);
  }, [
    props,
    closeLineCommentBox,
    lineCommentBoxVisibility,
    toggleLineCommentBoxVisibility,
  ]);

  const ext = langExt(props.code.language);
  const filename = props.code.title
    ? `${props.code.title.replace(/\s+/g, '-').toLowerCase()}.${ext}`
    : `code-${props.code.id ? props.code.id.slice(0, 8) : 'file'}.${ext}`;

  return (
    <div className="cs-editor">
      <div className="cs-editor__head">
        <BranchIcon />
        <span className="filename" title={filename}>{filename}</span>
        <span className="lang-tag">{props.code.language || t('code')}</span>
        <span className="head-spacer" />
        <span className="linecount">
          {codeRows.length} {t('lines')}
        </span>
      </div>
      <div className="cs-editor__scroll" ref={scrollRef}>
        <table
          cellSpacing="0"
          cellPadding="0"
          className="cs-code-table"
        >
          <tbody>
            {codeRows}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function langExt(language) {
  const m = {
    c_cpp: 'cpp',
    cpp: 'cpp',
    java: 'java',
    python: 'py',
    javascript: 'js',
    sql: 'sql',
  };
  return m[language] || 'txt';
}

function BranchIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ color: 'var(--fg-3)' }}
    >
      <path d="M6 3v12M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 15a9 9 0 0 0 9-9" />
    </svg>
  );
}

export default CodeTable;
